import { selectTerritoryLicensingLimits } from '@/features/domain/territory'
import { selectUserConfiguration } from '@/features/domain/user'
import { dateUtils } from '@/utils'
import { type DateCalendarProps } from '@mui/x-date-pickers/DateCalendar'
import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  isAfter,
  isValid,
  startOfMonth,
  subDays,
} from 'date-fns/esm'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useRecurrenceDate, useRecurrenceDispatch } from '../../../providers/StateProvider'

export function useCalendarProps() {
  const recurrenceDate = useRecurrenceDate()
  const dispatch = useRecurrenceDispatch()
  const { today } = useSelector(selectUserConfiguration)
  const { planningRange } = useSelector(selectTerritoryLicensingLimits)

  return useMemo(() => {
    const selection = dateUtils.parseDate(recurrenceDate)
    const todayDate = dateUtils.parseDate(today)
    const minDate = startOfMonth(todayDate)
    const maxPlanningDate = addDays(todayDate, planningRange)
    const maxDate = endOfMonth(maxPlanningDate)

    // disabled days are those before today and after the planning range
    const intervalFromStartOfMonthAndToday = {
      start: minDate,
      end: subDays(todayDate, 1),
    }

    const disabledDatesBeforeToday = isValidInterval(intervalFromStartOfMonthAndToday)
      ? eachDayOfInterval(intervalFromStartOfMonthAndToday)
      : []

    // disabled days after the planning range to the end of the month
    const intervalFromDayAfterPlanningRangeAndMaxDate = {
      start: addDays(maxPlanningDate, 1),
      end: maxDate,
    }

    const disabledDatesAfterRange = isValidInterval(intervalFromDayAfterPlanningRangeAndMaxDate)
      ? eachDayOfInterval(intervalFromDayAfterPlanningRangeAndMaxDate)
      : []

    const disabledDays = [...disabledDatesBeforeToday, ...disabledDatesAfterRange].reduce<
      Record<string, number>
    >((acc, d) => {
      acc[dateUtils.convertToYYYYMMDD(d)] = 1
      return acc
    }, {})

    const disabledDaysSet = new Set<uui.domain.YYYYMMDD>(
      Object.keys(disabledDays) as uui.domain.YYYYMMDD[],
    )

    const calendarProps: DateCalendarProps<Date> = {
      value: selection,
      onChange(date: Date) {
        dispatch({ type: 'recurrenceDate:change', recurrenceDate: date })
      },
      minDate,
      maxDate,
      shouldDisableDate(date: Date) {
        const formattedDate = dateUtils.convertToYYYYMMDD(date)
        return disabledDaysSet.has(formattedDate)
      },
    }

    return {
      calendarProps,
      disabledDays,
      minDateAsString: dateUtils.convertToYYYYMMDD(minDate),
      maxDateAsString: dateUtils.convertToYYYYMMDD(maxDate),
    }
  }, [today, dispatch, planningRange, recurrenceDate])
}

type Interval = {
  start: Date
  end: Date
}

function isValidInterval({ start, end }: Interval): boolean {
  return isValid(start) && isValid(end) && isAfter(end, start)
}
